/* eslint-disable react/prop-types */
import { useHomeScreenContext } from 'layouts/HomeScreenLayout/contexts/HomeScreenContext';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Table } from 'react-bootstrap';

import { getPrintPaperSize } from 'utils/printPaperSizes';

const getTableDataSignature = tableData => {
  try {
    return JSON.stringify(
      (tableData || []).map(row => ({
        colSpan: row?.colSpan,
        key: row?.key,
        rowData: row?.rowData,
        type: row?.type,
      }))
    );
  } catch (error) {
    return `${tableData?.length || 0}`;
  }
};

const PreviewPrintLayout = ({
  tableData,
  printLayoutHeader,
  printLayoutFooter,
  productionDetails,
  tableHeader,
  tableClassName,
  showTitle,
  classNamePrefix = '',
  tableProps,
  footer,
  columnWidths,
  paperSize = 'letter',
  paperOptions,
}) => {
  const { selectedBudget } = useHomeScreenContext();
  const [tableState, setTableState] = useState({
    currentRowIndex: 0,
    currentTableIndex: 0,
    tables: [],
  });
  const { currentRowIndex, currentTableIndex, tables } = tableState;
  const tableRefs = useRef([]);
  const className = (classNamePrefix ? classNamePrefix : '') + 'print-layout';
  const tableDataLength = tableData?.length || 0;
  const paperSizeConfig = useMemo(
    () => getPrintPaperSize(paperSize, paperOptions),
    [paperSize, paperOptions]
  );
  const tableDataSignature = useMemo(
    () => getTableDataSignature(tableData),
    [tableData]
  );

  const setRef = tableIndex => ref => {
    tableRefs.current[tableIndex] = ref;
  };

  useEffect(() => {
    tableRefs.current = [];

    setTableState({
      currentRowIndex: tableData?.length ? 1 : 0,
      currentTableIndex: 0,
      tables: tableData?.length ? [[0]] : [],
    });
  }, [paperSizeConfig.key, tableData?.length, tableDataSignature]);

  useEffect(() => {
    if (
      currentRowIndex >= 1 &&
      tableDataLength &&
      currentRowIndex <= tableDataLength
    ) {
      const currentTableHeight =
        tableRefs.current[currentTableIndex]?.clientHeight;

      const paginationTimer = setTimeout(() => {
        setTableState(previousState => {
          if (
            previousState.currentRowIndex !== currentRowIndex ||
            previousState.currentTableIndex !== currentTableIndex
          ) {
            return previousState;
          }

          const tableInfo = previousState.tables.map(table => [...table]);

          if (previousState.currentRowIndex > tableDataLength) {
            return previousState;
          }

          if (
            currentTableHeight > paperSizeConfig.maxTableHeightPixels &&
            tableInfo[currentTableIndex]?.length > 1
          ) {
            const lastRowIndex = tableInfo[currentTableIndex]?.pop();

            if (lastRowIndex === undefined) {
              return previousState;
            }

            return {
              currentRowIndex: previousState.currentRowIndex,
              currentTableIndex: currentTableIndex + 1,
              tables: [...tableInfo, [lastRowIndex]],
            };
          }

          if (previousState.currentRowIndex >= tableDataLength) {
            return {
              ...previousState,
              currentRowIndex: tableDataLength + 1,
            };
          }

          if (!tableInfo[currentTableIndex]) {
            return previousState;
          }

          if (tableInfo[currentTableIndex].includes(currentRowIndex)) {
            return previousState;
          }

          tableInfo[currentTableIndex].push(currentRowIndex);

          return {
            currentTableIndex: previousState.currentTableIndex,
            currentRowIndex: currentRowIndex + 1,
            tables: tableInfo,
          };
        });
      }, 0);

      return () => clearTimeout(paginationTimer);
    }
  }, [
    tableData?.length,
    tableDataLength,
    currentRowIndex,
    currentTableIndex,
    paperSizeConfig.maxTableHeightPixels,
    tableDataSignature,
  ]);

  const styles =
    currentRowIndex > tableDataLength
      ? { minHeight: `${paperSizeConfig.pageHeightPixels}px` }
      : {};

  return tables?.map((table, t) => {
    const tableFooter = [];
    const footerRows = footer?.filter(row => row.type === 'swimlaneFooter');
    const isLastPage = t === tables.length - 1; // Check if it's the last page

    return (
      <React.Fragment key={`print-page-fragment-${t}`}>
        <div
          key={`print-page-${t}`}
          className={className}
          ref={setRef(t)}
          style={styles}
        >
          {printLayoutHeader}
          {t === 0 && (
            <>
              {showTitle && (
                <div className={`${className}__title`}>
                  {selectedBudget?.name}
                </div>
              )}
              {productionDetails}
            </>
          )}
          <div className={`${className}__details`}>
            <Table
              className={tableClassName}
              style={{ marginBottom: '0rem' }}
              {...tableProps}
            >
              {columnWidths?.length > 0 && (
                <colgroup>
                  {columnWidths.map((width, columnIndex) => (
                    <col
                      key={`print-column-${columnIndex}`}
                      style={{ width }}
                    />
                  ))}
                </colgroup>
              )}
              {tableHeader}
              <tbody>
                {table?.map((rowIndex, index) => {
                  const row = tableData?.[rowIndex];

                  if (!row) {
                    return null;
                  }

                  if (row.type === 'swimlaneFooter') {
                    tableFooter.push(row);
                    return null;
                  }

                  return row.renderMethod(row.rowData, `${t}-${index}`);
                })}
              </tbody>
              {tableFooter?.length > 0 && (
                <tfoot>
                  {tableFooter?.map((row, index) =>
                    React.cloneElement(row.renderMethod(row.rowData), {
                      key: `table-footer-${t}-${index}`,
                    })
                  )}
                </tfoot>
              )}
            </Table>
          </div>

          {/* Render footer only on the last page */}
          {isLastPage && (
            <div className={`${className}__footRow`}>
              <Table className={tableClassName} {...tableProps}>
                {footerRows?.length > 0 && (
                  <tfoot>
                    {footerRows?.map((row, index) =>
                      React.cloneElement(row.renderMethod(row.rowData), {
                        key: `page-footer-${t}-${index}`,
                      })
                    )}
                  </tfoot>
                )}
              </Table>
            </div>
          )}

          {/* Spacer to push footer down */}
          <div style={{ flexGrow: 1 }} />

          {printLayoutFooter}
        </div>
        {t + 1 < tables.length && <div className="page-break" />}
      </React.Fragment>
    );
  });
};

export default PreviewPrintLayout;
